// Admin API client. Reads the rw_token cookie and sends as Bearer.
// Used only for admin actions (list/create/update/delete users, assign groups).

import { fetchAuthSession } from 'aws-amplify/auth';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function authHeader(): Promise<string> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new ApiError(401, 'Not signed in');
  return `Bearer ${idToken}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: auth,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface UserSummary {
  sub: string;
  email: string;
  name: string;
  status: string;
  enabled: boolean;
  createdAt: string;
  groups: string[];
}

export const adminApi = {
  listUsers: () => request<{ users: UserSummary[] }>('GET', '/api/users'),
  createUser: (input: { email: string; name: string; groups?: string[] }) =>
    request<{ user: UserSummary }>('POST', '/api/users', input),
  updateUser: (username: string, input: { name?: string }) =>
    request<{ user: UserSummary }>('PUT', `/api/users/${encodeURIComponent(username)}`, input),
  deleteUser: (username: string) =>
    request<{ deleted: boolean }>('DELETE', `/api/users/${encodeURIComponent(username)}`),
  addGroup: (username: string, group: string) =>
    request<{ user: UserSummary }>(
      'POST',
      `/api/users/${encodeURIComponent(username)}/groups`,
      { group },
    ),
  removeGroup: (username: string, group: string) =>
    request<{ user: UserSummary }>(
      'DELETE',
      `/api/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(group)}`,
    ),
  resendInvite: (username: string) =>
    request<{ resent: boolean }>(
      'POST',
      `/api/users/${encodeURIComponent(username)}/resend-invite`,
    ),
  listGroups: () =>
    request<{ groups: { name: string; description: string }[] }>('GET', '/api/groups'),
  me: () => request<{ user: UserSummary }>('GET', '/api/me'),
};
